import { useState } from 'react';
import { Alert, Box, Button, Chip, Collapse, Link, Stack, Typography } from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { useAdminWebhookAsaasQuery } from '../../api';
import { CampoCopiavel } from './campo-copiavel';
import { PainelInfo } from './painel-info';

/**
 * Instruções do webhook do ASAAS no editor de forma de pagamento ONLINE.
 *
 * Vincular a configuração (token) só resolve a IDA — o cardápio criando a
 * cobrança. A VOLTA (ASAAS avisando que o cliente pagou) depende de um webhook
 * cadastrado na conta ASAAS da loja; sem ele o pedido fica parado em
 * "aguardando pagamento" e nunca chega ao Sischef. Como isso é feito fora do
 * nosso sistema, a tela precisa entregar URL, token e o passo a passo.
 */
export function InstrucoesWebhookAsaas({ idConfig }: { idConfig: number }): JSX.Element {
  const { data, isError } = useAdminWebhookAsaasQuery({ idConfig });
  const [passosAbertos, setPassosAbertos] = useState(false);

  // Falhar em carregar não é ajuda, é problema: sem URL e token o gestor não
  // tem o que copiar, então isso sai como alerta e não como quadro de apoio.
  if (isError) {
    return (
      <Alert severity="warning" variant="outlined">
        Não foi possível carregar os dados do webhook do ASAAS. Recarregue a página ou fale com o suporte.
      </Alert>
    );
  }

  return (
    <PainelInfo titulo="Cadastre o webhook na conta ASAAS da loja" denso>
      <Typography variant="body2" sx={{ mb: 1.5 }}>
        É o webhook que avisa o cardápio quando o cliente paga. Sem ele o pedido fica parado em
        “aguardando pagamento” e não chega ao Sischef. Configure uma vez, no portal do ASAAS:
      </Typography>

      {/* Mesmo quadro durante o carregamento: só o miolo troca, então a tela não
          pula quando URL e token chegam. */}
      {!data ? (
        <Typography variant="body2">Carregando dados do webhook do ASAAS…</Typography>
      ) : (
        <Stack spacing={1.5}>
          <CampoCopiavel
            rotulo="URL do webhook"
            valor={data.url}
            ajuda="Cole no campo URL do webhook, no portal do ASAAS."
          />
          <CampoCopiavel
            rotulo="Token de autenticação"
            valor={data.token}
            ajuda="Token exclusivo desta loja. O ASAAS o envia de volta em cada aviso; se estiver errado, o aviso é recusado."
          />

          <Box>
            <Button
              size="small"
              color="inherit"
              onClick={() => setPassosAbertos((v) => !v)}
              endIcon={
                <ExpandMoreIcon
                  sx={{
                    transition: 'transform 200ms ease',
                    transform: passosAbertos ? 'rotate(180deg)' : 'none',
                  }}
                />
              }
              sx={{ px: 0 }}
            >
              {passosAbertos ? 'Ocultar passo a passo' : 'Ver passo a passo no ASAAS'}
            </Button>

            <Collapse in={passosAbertos} timeout={300} unmountOnExit>
              <Box component="ol" sx={{ pl: 2.5, mt: 1, mb: 0, '& li': { mb: 0.75 } }}>
                <li>
                  Entre no portal do ASAAS com a conta da loja (a mesma de onde saiu o token cadastrado no{' '}
                  <b>Sischef Retaguarda</b>).
                </li>
                <li>
                  Abra o menu da conta (canto superior direito) → <b>Integrações</b> → aba <b>Webhooks</b> e clique em
                  adicionar um novo webhook.
                </li>
                <li>
                  Dê um nome (ex.: <i>Cardápio Sischef</i>), cole a <b>URL</b> acima e informe um e-mail para receber
                  avisos de falha.
                </li>
                <li>
                  Cole o valor acima no campo <b>Token de autenticação</b> e mantenha a versão da API em <b>v3</b>.
                </li>
                <li>
                  Marque os eventos de cobrança abaixo:
                  <Stack direction="row" flexWrap="wrap" gap={0.5} sx={{ mt: 0.75 }}>
                    {data.eventos.map((evento) => (
                      <Chip key={evento} label={evento} size="small" variant="outlined" sx={{ fontSize: 11 }} />
                    ))}
                  </Stack>
                </li>
                <li>
                  Deixe o webhook <b>ativo</b> e salve. Depois de várias falhas seguidas o ASAAS interrompe o envio — se
                  os pagamentos pararem de confirmar, confira nessa mesma tela se ele continua ativo.
                </li>
              </Box>
              <Typography variant="caption" color="text.secondary" component="p" sx={{ mt: 1 }}>
                Documentação do ASAAS:{' '}
                <Link href="https://docs.asaas.com/docs/about-webhooks" target="_blank" rel="noopener">
                  docs.asaas.com/docs/about-webhooks
                </Link>
              </Typography>
            </Collapse>
          </Box>
        </Stack>
      )}
    </PainelInfo>
  );
}
